import {
  deletePublicShareSelection,
  deleteUserSelection,
  getDownloadRequests,
  getPublicShareDownloadRequests,
  getPublicShareSelectionAvailableExportFormats,
  getPublicShareSelectionMedias,
  getPublicShareSelectionMediasTermsOfUse,
  getUserSelectionAvailableExportFormats,
  getUserSelectionMedias,
  getUserSelectionMediasTermsOfUse,
  KpkApiDeleteUserSelectionParams,
  KpkApiEmbedded,
  KpkApiGetDownloadRequestsParams,
  KpkApiGetDownloadRequestsResult,
  KpkApiGetSelectionAvailableExportFormatsResult,
  KpkApiGetUserSelectionAvailableExportFormatsParams,
  KpkApiGetUserSelectionMediasParams,
  KpkApiPagedResult,
  KpkApiPostDownloadRequestsParams,
  KpkApiPostDownloadValidationRequestsParams,
  KpkApiPostSelectionResult,
  KpkApiPostUserSelectionMediasCollectionParams,
  KpkApiPostUserSelectionParams,
  KpkApiSelectionMediasResult,
  KpkApiSelectionMediasTermsOfUseEmbeddedMapping,
  postDownloadRequests,
  postPublicShareDownloadRequests,
  postPublicShareSelection,
  postPublicShareSelectionMediasCollection,
  postUserSelection,
  postUserSelectionMediasCollection,
  postDownloadValidationRequests,
  postPublicDownloadValidationRequests,
  getPublicSharedMedia,
  KpkApiMedia,
} from "@keepeek/api-client";
import {
  buildParamsForShare,
  FetcherMode,
  fetcherTokenIdAtom,
  shareTokenAxiosHeadersSelector,
  isAuthenticatedSelector,
  isPublicVisitorState,
  useShare,
} from "@keepeek/commons";
import { AxiosResponse } from "axios";
import { useRouter } from "next/router";
import { selectorFamily } from "recoil";

import { getAxiosClientInstance } from "../../../lib/axios/axios-utils";

export type DownloadManagerFetcher = {
  postSelection: (
    params: KpkApiPostUserSelectionParams,
  ) => Promise<AxiosResponse<KpkApiPostSelectionResult>> | Promise<undefined>;
  postSelectionMediasCollection: (
    params: KpkApiPostUserSelectionMediasCollectionParams,
  ) => Promise<AxiosResponse> | Promise<undefined>;
  getSelectionAvailableExportFormats: (
    params: KpkApiGetUserSelectionAvailableExportFormatsParams,
  ) => Promise<AxiosResponse<KpkApiGetSelectionAvailableExportFormatsResult>> | Promise<undefined>;
  getSelectionMediasTermsOfUse: (
    params: KpkApiGetUserSelectionMediasParams,
  ) =>
    | Promise<AxiosResponse<KpkApiEmbedded<KpkApiSelectionMediasTermsOfUseEmbeddedMapping>>>
    | Promise<undefined>;
  getSelectionMedias: (
    params: KpkApiGetUserSelectionMediasParams,
  ) =>
    | Promise<AxiosResponse<KpkApiPagedResult<KpkApiSelectionMediasResult>>>
    | Promise<AxiosResponse<KpkApiMedia>>
    | Promise<undefined>;
  deleteSelection: (
    params: KpkApiDeleteUserSelectionParams,
  ) => Promise<AxiosResponse> | Promise<undefined>;
  getDownloadRequests: (
    params: KpkApiGetDownloadRequestsParams,
  ) => Promise<AxiosResponse<KpkApiGetDownloadRequestsResult>> | Promise<undefined>;
  postDownloadRequests: (
    params: KpkApiPostDownloadRequestsParams,
  ) => Promise<AxiosResponse> | Promise<undefined>;
  postDownloadValidationRequests: (
    params: KpkApiPostDownloadValidationRequestsParams,
  ) => Promise<AxiosResponse> | Promise<undefined> | Promise<unknown>;
};

export const downloadManagerFetcherSelector = selectorFamily<DownloadManagerFetcher, FetcherMode>({
  key: "DownloadManagerFetcherSelector",
  get:
    (mode) =>
    ({ get }) => {
      const isVisitor = get(isPublicVisitorState);
      switch (mode) {
        case FetcherMode.SHARE:
          const tokenId = get(fetcherTokenIdAtom);
          const headers = get(shareTokenAxiosHeadersSelector);
          const router = useRouter();
          const { isUnitaryShareElement } = useShare(router.pathname, router.query.tokenId);

          return {
            postSelection: (params) => {
              return postPublicShareSelection(
                getAxiosClientInstance(),
                buildParamsForShare(params, headers, tokenId),
              );
            },
            postSelectionMediasCollection: (params) => {
              return postPublicShareSelectionMediasCollection(
                getAxiosClientInstance(),
                buildParamsForShare(params, headers, tokenId),
              );
            },
            getSelectionAvailableExportFormats: (params) => {
              return getPublicShareSelectionAvailableExportFormats(
                getAxiosClientInstance(),
                buildParamsForShare(params, headers, tokenId),
              );
            },
            getSelectionMediasTermsOfUse: (params) => {
              return getPublicShareSelectionMediasTermsOfUse(
                getAxiosClientInstance(),
                buildParamsForShare(params, headers, tokenId),
              );
            },
            getSelectionMedias: (params) => {
              if (isUnitaryShareElement) {
                return getPublicSharedMedia(
                  getAxiosClientInstance(),
                  buildParamsForShare(params, headers, tokenId),
                );
              }
              return getPublicShareSelectionMedias(
                getAxiosClientInstance(),
                buildParamsForShare(params, headers, tokenId),
              );
            },
            deleteSelection: (params) => {
              return deletePublicShareSelection(
                getAxiosClientInstance(),
                buildParamsForShare(params, headers, tokenId),
              );
            },
            getDownloadRequests: (params) => {
              return getPublicShareDownloadRequests(
                getAxiosClientInstance(),
                buildParamsForShare(params, headers, tokenId),
              );
            },
            postDownloadRequests: (params) => {
              return postPublicShareDownloadRequests(
                getAxiosClientInstance(),
                buildParamsForShare(params, headers, tokenId),
              );
            },
            postDownloadValidationRequests: (params) => {
              return postPublicDownloadValidationRequests(getAxiosClientInstance(), params);
            },
          };
        default:
          const isAuthenticated = get(isAuthenticatedSelector);
          const postDownloadValidationRequestsLoggedFunction = (params) => {
            if (!isAuthenticated) {
              return Promise.resolve(undefined);
            }
            return postDownloadValidationRequests(getAxiosClientInstance(), params);
          };
          const postDownloadValidationRequestsPublicFunction = (params) => {
            return postPublicDownloadValidationRequests(getAxiosClientInstance(), params);
          };
          let postDownloadValidationRequestsVar;
          if (isVisitor) {
            postDownloadValidationRequestsVar = postDownloadValidationRequestsPublicFunction;
          } else {
            postDownloadValidationRequestsVar = postDownloadValidationRequestsLoggedFunction;
          }
          return {
            postSelection: (params) => {
              if (!isAuthenticated) {
                return Promise.resolve(undefined);
              }
              return postUserSelection(getAxiosClientInstance(), params);
            },
            postSelectionMediasCollection: (params) => {
              if (!isAuthenticated) {
                return Promise.resolve(undefined);
              }
              return postUserSelectionMediasCollection(getAxiosClientInstance(), params);
            },
            getSelectionAvailableExportFormats: (params) => {
              if (!isAuthenticated) {
                return Promise.resolve(undefined);
              }
              return getUserSelectionAvailableExportFormats(getAxiosClientInstance(), params);
            },
            getSelectionMediasTermsOfUse: (params) => {
              if (!isAuthenticated) {
                return Promise.resolve(undefined);
              }
              return getUserSelectionMediasTermsOfUse(getAxiosClientInstance(), params);
            },
            getSelectionMedias: (params) => {
              if (!isAuthenticated) {
                return Promise.resolve(undefined);
              }
              return getUserSelectionMedias(getAxiosClientInstance(), params);
            },
            deleteSelection: (params) => {
              if (!isAuthenticated) {
                return Promise.resolve(undefined);
              }
              return deleteUserSelection(getAxiosClientInstance(), params);
            },
            getDownloadRequests: (params) => {
              if (!isAuthenticated) {
                return Promise.resolve(undefined);
              }
              return getDownloadRequests(getAxiosClientInstance(), params);
            },
            postDownloadRequests: (params) => {
              if (!isAuthenticated) {
                return Promise.resolve(undefined);
              }
              return postDownloadRequests(getAxiosClientInstance(), params);
            },
            postDownloadValidationRequests: postDownloadValidationRequestsVar,
          };
      }
    },
});
